/**
 * CompanyRevenueTable — iter-224 (Feb 2026)
 *
 * Unified ledger for "إيرادات نظام الوكلاء والمدفوعات الحكومية والعمولة"
 * (rebranded from the old "ايراد منصة التجار" per user's explicit request).
 *
 * Route: /dashboard/finance/cloudgm/merchants/tax-invoices
 *
 * Merges the data previously spread across:
 *   • Merchant Platform Revenue (receipts table)
 *   • Government Fees Report
 *   • Merchant Commission Follow-up
 *
 * Table columns (per user's spec):
 *   1. رقم الطلب              — request_no (AGNT-YYYY-XXXXXX)
 *   2. رقم الإيصال            — receipt_no (CGB-YYYY-XXXXXXX)
 *   3. الدافع                 — payer_name + email + phone (from
 *                                 requestor card, NOT the agent)
 *   4. الخدمة                 — main service name
 *   5. المبلغ المستلم         — amount_aed (customer's total)
 *   6. الرسوم الحكومية        — task.gov_fees_total
 *   7. رسوم الخدمة            — task.service_fee
 *   8. عمولة الإيجنت          — task.commission_amount
 *   9. صافي الربح             — amount - gov - service - commission
 *  10. إجراءات                — Print + Open source task
 *
 * KPI cards (same size, side-by-side):
 *   • Total Revenue {year}
 *   • Total Gov Fees Paid {year}
 *   • Total Commission Sent {year}
 *   • Net Profit {year}
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Receipt, Search, Loader2, Printer, ExternalLink,
  TrendingUp, Download, Landmark, Percent, Wallet,
  CheckCircle2, XCircle, RefreshCcw, X,
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { printReceipt } from '../utils/printReceipt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt2 = (v) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CompanyRevenueTable() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    revenue: 0, gov: 0, commission: 0, service: 0, net: 0,
  });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [q, setQ] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  // iter-237.3 (Feb 2026) — Data source switch: "agent_tasks" (default,
  // reads /api/admin/receipts) OR "company_registrations" (reads
  // /api/admin/company-registrations/revenue). Columns and totals adapt.
  const [dataMode, setDataMode] = useState('agent_tasks');
  // iter-227 (Feb 2026) — Per user's rule: "in order to display the invoice
  // record in the same screen, the request must have passed the audit
  // condition". Rows failing audit are hidden by default. Operators can
  // toggle "showFailed" to see them and fix the mismatches.
  // iter-257 (Feb 2026) — Replaced the single "show unaudited" checkbox
  // with a 3-tab filter: all | audited | unaudited (default = all).
  const [auditFilter, setAuditFilter] = useState('all');   // 'all'|'audited'|'unaudited'
  // iter-234 (Feb 2026) — Language modal before printing (user requested).
  const [printTarget, setPrintTarget] = useState(null);   // row or null
  // iter-257 (Feb 2026) — Consistency stats: closed source records vs receipts.
  const [statsBox, setStatsBox] = useState(null);

  // Filtered view — hides rows that don't pass the audit gate unless the
  // "show unaudited" checkbox is on. All KPI totals still reflect the FULL
  // dataset from the backend (they aggregate everything regardless of the
  // client-side filter).
  // iter-245 (Feb 2026) — Audit gating applies to `agent_tasks` ONLY.
  // Company-registration requests are created straight through without
  // audit (user's rule: audit is only for manual agent tasks). In that
  // mode we always show every row.
  const auditApplies = dataMode === 'agent_tasks';
  const visibleRows = useMemo(() => {
    if (!auditApplies) return rows;
    if (auditFilter === 'audited')   return rows.filter((r) => r.audit_ok !== false);
    if (auditFilter === 'unaudited') return rows.filter((r) => r.audit_ok === false);
    return rows;                                        // 'all'
  }, [rows, auditFilter, auditApplies]);
  const auditedCount   = useMemo(() => rows.filter((r) => r.audit_ok !== false).length, [rows]);
  const unauditedCount = useMemo(() => rows.filter((r) => r.audit_ok === false).length, [rows]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (year) params.year = year;
      if (q.trim()) params.q = q.trim();
      // iter-237.3 — pick endpoint based on the switch.
      const url = dataMode === 'company_registrations'
        ? `${BACKEND_URL}/api/admin/company-registrations/revenue`
        : `${BACKEND_URL}/api/admin/receipts`;
      const r = await axios.get(url, { headers: getAuthHeaders(), params });
      setRows(r.data?.data || []);
      if (dataMode === 'company_registrations') {
        // Backend key: total_admin_fees_aed replaces total_service_fees_aed.
        setTotals({
          revenue:    Number(r.data?.total_aed || 0),
          gov:        Number(r.data?.total_gov_fees_aed || 0),
          commission: Number(r.data?.total_agent_commission_aed || 0),
          service:    Number(r.data?.total_admin_fees_aed || 0),
          net:        Number(r.data?.total_net_profit_aed || 0),
        });
      } else {
        setTotals({
          revenue:    Number(r.data?.total_aed || 0),
          gov:        Number(r.data?.total_gov_fees_aed || 0),
          commission: Number(r.data?.total_agent_commission_aed || 0),
          service:    Number(r.data?.total_service_fees_aed || 0),
          net:        Number(r.data?.total_net_profit_aed || 0),
        });
      }
    } catch (e) {
      toast.error(t('فشل تحميل الإيرادات', 'Failed to load revenue'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [year, dataMode]);
  useEffect(() => {
    const id = setTimeout(fetchRows, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [q]);

  // iter-257 — Consistency stats: closed source records vs receipts.
  const fetchStats = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/receipts/stats`,
                                 { headers: getAuthHeaders() });
      setStatsBox(r.data || null);
    } catch (e) {
      // Non-blocking — the rest of the page keeps working.
    }
  };
  useEffect(() => { fetchStats(); }, []);

  const availableYears = useMemo(() => {
    const y = new Date().getFullYear();
    return [y, y - 1, y - 2, y - 3];
  }, []);

  // iter-228 (Feb 2026) — Re-sync ALL receipts against each task's current
  // sum of `payments[].amount`. Fixes historical rows whose `amount_aed`
  // was frozen at the first-print value (e.g. AGNT-2026-000004 which had
  // two 15k instalments but the receipt kept only the first). Backend
  // never lowers a receipt — only bumps forward — so it's safe to run
  // repeatedly.
  const resyncPayments = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      // iter-257 (Feb 2026) — Chained maintenance:
      //   1) Re-sync existing receipts against current payment sums.
      //   2) Backfill: mint receipts for CLOSED tasks that never had one
      //      (fixes AGNT-2026-000003 style mismatches).
      //   3) Cleanup: purge orphan receipts whose source task/reg was deleted
      //      (fixes AGNT-2026-000018 style ghosts).
      //   4) iter-258 — Repair metadata on existing receipts (payer_name,
      //      request_no_display, service names) using the correct source
      //      fields from assigned_tasks. Fixes wrong-payer-info bugs.
      const [resSync, resFill, resCleanup, resRepair] = await Promise.all([
        axios.post(`${BACKEND_URL}/api/admin/receipts/resync-payments`,
                   {}, { headers: getAuthHeaders() }),
        axios.post(`${BACKEND_URL}/api/admin/receipts/backfill-closed`,
                   {}, { headers: getAuthHeaders() }),
        axios.post(`${BACKEND_URL}/api/admin/receipts/cleanup-orphans`,
                   {}, { headers: getAuthHeaders() }),
        axios.post(`${BACKEND_URL}/api/admin/receipts/repair-metadata`,
                   {}, { headers: getAuthHeaders() }),
      ]);
      const bumped  = Number(resSync.data?.bumped || 0);
      const checked = Number(resSync.data?.checked || 0);
      const created = Number(resFill.data?.created || 0);
      const removed = Number(resCleanup.data?.deleted || 0);
      const repaired = Number(resRepair.data?.updated || 0);
      toast.success(t(
        `مزامنة: ${bumped}/${checked} · إنشاء: ${created} · يتيم: ${removed} · إصلاح: ${repaired}`,
        `Sync: ${bumped}/${checked} · Backfill: ${created} · Orphans: ${removed} · Repair: ${repaired}`,
      ));
      await fetchRows();
      await fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشلت المزامنة', 'Sync failed'));
    } finally {
      setSyncing(false);
    }
  };

  const exportCsv = () => {
    if (!rows.length) return;
    const hdr = [
      'Request No', 'Receipt No', 'Payer', 'Email', 'Phone',
      'Service', 'Amount (AED)', 'Gov Fees (AED)', 'Service Fees (AED)',
      'Agent Commission (AED)', 'Net Profit (AED)', 'Audit',
      'Audit Delta (AED)', 'Issued at',
    ];
    const csv = [
      hdr.join(','),
      ...rows.map((r) => [
        r.request_no || '', r.receipt_no || '', r.payer_name || '',
        r.payer_email || '', r.payer_phone || '',
        (r.service_name_en || r.service_name_ar || '').replace(/,/g, ' '),
        fmt2(r.amount_aed), fmt2(r.gov_fees_aed),
        fmt2(r.service_fee_aed), fmt2(r.agent_commission_aed),
        fmt2(r.net_profit_aed),
        r.audit_ok ? 'OK' : 'MISMATCH',
        fmt2(r.audit_delta_aed || 0),
        r.created_at || '',
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `cloudgm-revenue-${year}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout
      title="إيرادات نظام الوكلاء والمدفوعات الحكومية والعمولة"
      titleEn="Agent System Revenue, Gov Fees & Commission"
      subtitle="جدول موحد بجميع الإيصالات مع تفصيل الرسوم والعمولات وصافي الربح"
      subtitleEn="Unified ledger of all receipts with fees, commissions and net profit breakdown"
      screenCode="FIN-MERCH-REV"
      backPath="/dashboard/finance/cloudgm/merchants"
      breadcrumb={[
        { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
        { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/dashboard/finance/cloudgm' },
        { labelAr: 'منصة التجار (المالية)', labelEn: 'Merchant Platform (Finance)', path: '/dashboard/finance/cloudgm/merchants' },
        { labelAr: 'إيرادات نظام الوكلاء والمدفوعات الحكومية والعمولة', labelEn: 'Agent System Revenue' },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="company-revenue-table">
        {/* iter-257b (Feb 2026) — Consistency box: moved to the TOP of
            the page and split by current mode. Compact single-cell
            widget matching the active data source (agent_tasks vs
            company_registrations). Shows closed_source → receipts_here
            with an amber alert on any mismatch. */}
        {statsBox && (
          <div className="mb-5 max-w-md" data-testid="revenue-consistency-box">
            {dataMode === 'agent_tasks' ? (
              <StatsBoxCell
                title={t('مقارنة مهام الوكلاء', 'Agent Tasks Consistency')}
                closedLabel={t('المهام المغلقة (تتبّع)', 'Closed tasks (tracking)')}
                closed={statsBox?.agent_tasks?.closed_count || 0}
                receipts={statsBox?.agent_tasks?.receipts_count || 0}
                t={t}
                testid="consistency-agent-tasks"
              />
            ) : (
              <StatsBoxCell
                title={t('مقارنة تسجيل الشركات', 'Company Registrations Consistency')}
                closedLabel={t('التسجيلات المغلقة (تتبّع)', 'Closed registrations (tracking)')}
                closed={statsBox?.company_registrations?.closed_count || 0}
                receipts={statsBox?.company_registrations?.receipts_count || 0}
                t={t}
                testid="consistency-company-registrations"
              />
            )}
          </div>
        )}

        {/* KPI Cards — 4 same-size, side-by-side */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <KpiCard
            label={t(`إجمالي الإيرادات ${year}`, `Total Revenue ${year}`)}
            value={totals.revenue}
            Icon={TrendingUp}
            color="#10b981"
            testId="kpi-total-revenue"
          />
          <KpiCard
            label={t(`إجمالي الرسوم الحكومية المدفوعة ${year}`, `Total Gov Fees ${year}`)}
            value={totals.gov}
            Icon={Landmark}
            color="#f59e0b"
            testId="kpi-total-gov"
          />
          <KpiCard
            label={t(`إجمالي العمولة المرسلة ${year}`, `Total Commission Sent ${year}`)}
            value={totals.commission}
            Icon={Percent}
            color="#8b5cf6"
            testId="kpi-total-commission"
          />
          <KpiCard
            label={t(`صافي الربح ${year}`, `Net Profit ${year}`)}
            value={totals.net}
            Icon={Wallet}
            color="#3B9EDC"
            testId="kpi-net-profit"
          />
        </div>

        {/* iter-237.3 (Feb 2026) — Data source switch: Agent Tasks vs Company Registrations */}
        <div className="mb-5 inline-flex bg-black/40 border border-white/10 rounded-xl p-1" data-testid="revenue-mode-switch">
          <button
            type="button"
            onClick={() => setDataMode('agent_tasks')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
              dataMode === 'agent_tasks'
                ? 'bg-[#d4af37] text-black'
                : 'text-white/60 hover:text-white'
            }`}
            data-testid="revenue-mode-agent-tasks"
          >
            {t('مهام الوكلاء', 'Agent Tasks')}
          </button>
          <button
            type="button"
            onClick={() => setDataMode('company_registrations')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
              dataMode === 'company_registrations'
                ? 'bg-[#d4af37] text-black'
                : 'text-white/60 hover:text-white'
            }`}
            data-testid="revenue-mode-company-registrations"
          >
            {t('تسجيل الشركات', 'Company Registrations')}
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-3 items-end flex-wrap mb-5">
          <div className="flex-1 min-w-[240px]">
            <label className="block text-[11px] text-white/50 mb-1">{t('بحث', 'Search')}</label>
            <div className="relative">
              <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-white/40" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('رقم الإيصال، رقم الطلب، اسم الدافع…', 'Receipt no, request no, payer…')}
                className="ps-9 bg-[#0a0a0a] border-white/10 text-white h-10"
                data-testid="revenue-search-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-[11px] text-white/50 mb-1">{t('السنة', 'Year')}</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-10 bg-[#0a0a0a] border border-white/10 text-white rounded-md px-3 text-sm"
              data-testid="revenue-year-filter"
            >
              {availableYears.map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
          </div>
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={!rows.length}
            className="border-white/15 bg-white/[0.03] text-white hover:bg-white/[0.06] h-10"
            data-testid="revenue-export-csv"
          >
            <Download className="w-4 h-4 me-1.5" />
            {t('تصدير CSV', 'Export CSV')}
          </Button>
          {/* iter-228 (Feb 2026) — Re-sync historical receipts against
              each task's current sum of payments. Fixes rows whose
              amount_aed is frozen at the first-print value even though
              additional instalments were recorded afterwards. */}
          <Button
            variant="outline"
            onClick={resyncPayments}
            disabled={syncing || !rows.length}
            className="border-amber-500/40 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20 h-10"
            data-testid="revenue-resync-payments"
          >
            {syncing
              ? <Loader2 className="w-4 h-4 animate-spin me-1.5" />
              : <RefreshCcw className="w-4 h-4 me-1.5" />}
            {t('مزامنة المدفوعات', 'Re-sync payments')}
          </Button>
        </div>

        {/* iter-257b — Old dual-mode stats box removed here; the split
            per-mode compact widget now lives at the top of the page. */}

        {/* iter-257 (Feb 2026) — 3-tab audit filter replaces the old checkbox.
            Applies to agent_tasks mode ONLY. Default = all. */}
        {auditApplies && (
          <div className="mb-4 inline-flex bg-black/40 border border-white/10 rounded-xl p-1"
               data-testid="revenue-audit-filter">
            <button
              type="button"
              onClick={() => setAuditFilter('all')}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition ${
                auditFilter === 'all'
                  ? 'bg-white/10 text-white shadow'
                  : 'text-white/60 hover:text-white/85'
              }`}
              data-testid="revenue-audit-filter-all"
            >
              {t('الكل', 'All')} ({rows.length})
            </button>
            <button
              type="button"
              onClick={() => setAuditFilter('audited')}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition ${
                auditFilter === 'audited'
                  ? 'bg-emerald-500/15 text-emerald-300 shadow'
                  : 'text-white/60 hover:text-white/85'
              }`}
              data-testid="revenue-audit-filter-audited"
            >
              {t('المدقّق', 'Audited')} ({auditedCount})
            </button>
            <button
              type="button"
              onClick={() => setAuditFilter('unaudited')}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition ${
                auditFilter === 'unaudited'
                  ? 'bg-amber-500/15 text-amber-300 shadow'
                  : 'text-white/60 hover:text-white/85'
              }`}
              data-testid="revenue-audit-filter-unaudited"
            >
              {t('غير المدقّق', 'Unaudited')} ({unauditedCount})
            </button>
          </div>
        )}

        {/* Table */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          {loading ? (
            <div className="p-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-white/40" /></div>
          ) : rows.length === 0 ? (
            <div className="p-16 text-center text-white/40" data-testid="revenue-empty">
              <Receipt className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">
                {dataMode === 'company_registrations'
                  ? t('لا توجد طلبات تسجيل شركات بعد.',
                     'No company registrations yet.')
                  : t('لا توجد إيصالات بعد. الإيصالات تنشأ تلقائياً عند طباعة/تحميل إيصال قبض من أي مهمة يدوية مسدَّدة بالكامل.',
                     'No receipts yet. Receipts appear automatically after you print/download a receipt from a fully-paid manual task.')}
              </p>
            </div>
          ) : visibleRows.length === 0 && auditApplies ? (
            <div className="p-16 text-center text-white/40" data-testid="revenue-all-failed">
              <Receipt className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">
                {auditFilter === 'audited'
                  ? t(`لا توجد إيصالات مدقّقة (${rows.length} إجمالي، ${unauditedCount} فشل التدقيق).`,
                     `No audited receipts (${rows.length} total, ${unauditedCount} failed audit).`)
                  : auditFilter === 'unaudited'
                  ? t(`لا توجد إيصالات فشلت التدقيق (${auditedCount} مدقّق).`,
                     `No unaudited receipts (${auditedCount} audited).`)
                  : t('لا توجد إيصالات', 'No receipts')}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03] border-b border-white/10">
                  <tr className="text-xs text-white/60 uppercase tracking-wider">
                    <th className="text-start px-3 py-3 whitespace-nowrap">{t('رقم الطلب', 'Request No')}</th>
                    <th className="text-start px-3 py-3 whitespace-nowrap">{t('رقم الإيصال', 'Receipt No')}</th>
                    <th className="text-start px-3 py-3">{t('الدافع', 'Payer')}</th>
                    <th className="text-start px-3 py-3">{t('الخدمة', 'Service')}</th>
                    <th className="text-end px-3 py-3 whitespace-nowrap">
                      {dataMode === 'company_registrations'
                        ? t('رسوم الرخصة', 'License Fees')
                        : t('المبلغ المستلم', 'Received')}
                    </th>
                    <th className="text-end px-3 py-3 whitespace-nowrap">{t('الرسوم الحكومية', 'Gov Fees')}</th>
                    {dataMode === 'company_registrations' ? (
                      <>
                        <th className="text-end px-3 py-3 whitespace-nowrap">{t('الرسوم الخاصة بنا', 'Our Fees')}</th>
                        <th className="text-end px-3 py-3 whitespace-nowrap">{t('عمولة الشركة', 'Company Commission')}</th>
                      </>
                    ) : (
                      <th className="text-end px-3 py-3 whitespace-nowrap">{t('رسوم الخدمة', 'Service Fees')}</th>
                    )}
                    <th className="text-end px-3 py-3 whitespace-nowrap">{t('عمولة الوكيل', 'Agent Commission')}</th>
                    <th className="text-end px-3 py-3 whitespace-nowrap">{t('صافي الربح', 'Net Profit')}</th>
                    {/* iter-257 — Audit column hidden in company_registrations mode (user's rule). */}
                    {dataMode !== 'company_registrations' && (
                      <th className="text-center px-3 py-3 whitespace-nowrap">{t('التدقيق', 'Audit')}</th>
                    )}
                    <th className="text-center px-3 py-3">{t('إجراءات', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => (
                    <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02] transition"
                        data-testid={`revenue-row-${r.receipt_no || r.request_no}`}>
                      <td className="px-3 py-3 text-white/70 font-mono text-xs whitespace-nowrap">
                        {r.request_no || '—'}
                      </td>
                      <td className="px-3 py-3 font-mono text-emerald-300 font-bold text-xs whitespace-nowrap">
                        {r.receipt_no || '—'}
                      </td>
                      <td className="px-3 py-3 text-white">
                        <div className="font-medium">{r.payer_name || '—'}</div>
                        {(r.payer_phone || r.payer_email) && (
                          <div className="text-[11px] text-white/40 flex flex-col gap-0.5 mt-0.5">
                            {r.payer_phone && <span dir="ltr">{r.payer_phone}</span>}
                            {r.payer_email && <span dir="ltr" className="truncate max-w-[220px]">{r.payer_email}</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-white/80">
                        {(isAr
                          ? (r.service_name_ar || r.service_name_en)
                          : (r.service_name_en || r.service_name_ar)) || '—'}
                      </td>
                      {/* Received / License Fees — string when paid direct to gov (company_registrations mode) */}
                      <td className="px-3 py-3 text-end font-mono font-bold whitespace-nowrap">
                        {dataMode === 'company_registrations' ? (
                          r.license_display_ar || r.license_display_en
                            ? <span className="text-amber-300 text-[11px] font-normal">
                                {isAr ? r.license_display_ar : r.license_display_en}
                              </span>
                            : <span className="text-white">{fmt2(r.license_fees_aed || r.amount_aed || 0)}</span>
                        ) : (
                          r.amount_display_ar || r.amount_display_en
                            ? <span className="text-amber-300 text-[11px] font-normal">
                                {isAr ? r.amount_display_ar : r.amount_display_en}
                              </span>
                            : <span className="text-white">{fmt2(r.amount_aed)}</span>
                        )}
                      </td>
                      {/* Gov Fees — string when paid direct to gov */}
                      <td className="px-3 py-3 text-end font-mono whitespace-nowrap">
                        {r.gov_fees_display_ar || r.gov_fees_display_en
                          ? <span className="text-amber-300 text-[11px]">
                              {isAr ? r.gov_fees_display_ar : r.gov_fees_display_en}
                            </span>
                          : <span className="text-amber-300">{fmt2(r.gov_fees_aed)}</span>}
                      </td>
                      {dataMode === 'company_registrations' ? (
                        <>
                          <td className="px-3 py-3 text-end font-mono whitespace-nowrap">
                            {r.admin_fees_display_ar || r.admin_fees_display_en
                              ? <span className="text-amber-300 text-[11px]">
                                  {isAr ? r.admin_fees_display_ar : r.admin_fees_display_en}
                                </span>
                              : <span className="text-blue-300">{fmt2(r.admin_fees_aed)}</span>}
                          </td>
                          <td className="px-3 py-3 text-end font-mono text-fuchsia-300 whitespace-nowrap">
                            {fmt2(r.company_commission_aed)}
                          </td>
                        </>
                      ) : (
                        <td className="px-3 py-3 text-end font-mono text-blue-300 whitespace-nowrap">
                          {fmt2(r.service_fee_aed)}
                        </td>
                      )}
                      {/* Agent Commission — "بدون وكيل" when no agent */}
                      <td className="px-3 py-3 text-end font-mono whitespace-nowrap">
                        {r.agent_commission_display_ar || r.agent_commission_display_en
                          ? <span className="text-white/50 text-[11px] italic">
                              {isAr ? r.agent_commission_display_ar : r.agent_commission_display_en}
                            </span>
                          : <span className="text-purple-300">{fmt2(r.agent_commission_aed)}</span>}
                      </td>
                      <td className="px-3 py-3 text-end font-mono font-bold whitespace-nowrap"
                          style={{ color: (r.net_profit_aed || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                        {fmt2(r.net_profit_aed)}
                      </td>
                      {dataMode !== 'company_registrations' && (
                        <td className="px-3 py-3 text-center">
                          <AuditCell r={r} t={t} />
                        </td>
                      )}
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => setPrintTarget(r)}
                            title={t('طباعة', 'Print')}
                            className="p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white"
                            data-testid={`revenue-print-${r.receipt_no || r.request_no}`}
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                          {/* Open source: registration → CompanyRegistrationDetailPage; task → assigned task */}
                          {(r.registration_id || r.task_id) && (
                            <button
                              type="button"
                              onClick={() => navigate(
                                r.registration_id
                                  ? `/manage/cloudgm-agent/company-registration/track/${r.registration_id}`
                                  : `/dashboard/cloudgm/assigned-tasks/${r.task_id}`,
                              )}
                              title={t('فتح الطلب الأصلي', 'Open source request')}
                              className="p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white"
                              data-testid={`revenue-open-source-${r.receipt_no || r.request_no}`}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* iter-234 — Language picker modal for print receipt. */}
      {printTarget && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
             onClick={() => setPrintTarget(null)} data-testid="revenue-print-lang-modal">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0a0a19] p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-base flex items-center gap-2">
                <Printer className="w-4 h-4 text-emerald-300" />
                {t('اختر لغة الإيصال', 'Choose receipt language')}
              </h3>
              <button type="button" onClick={() => setPrintTarget(null)} className="text-white/40 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => { printReceipt({ ...printTarget, status: 'paid' }, { language: 'ar' }); setPrintTarget(null); }}
                className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 font-semibold flex flex-col items-center gap-1"
                data-testid="revenue-print-lang-ar"
              >
                <Printer className="w-5 h-5" />
                <span>عربي</span>
                <span className="text-[10px] text-emerald-300/70 font-mono">AR</span>
              </button>
              <button
                type="button"
                onClick={() => { printReceipt({ ...printTarget, status: 'paid' }, { language: 'en' }); setPrintTarget(null); }}
                className="p-4 rounded-xl border border-sky-500/30 bg-sky-500/10 hover:bg-sky-500/20 text-sky-200 font-semibold flex flex-col items-center gap-1"
                data-testid="revenue-print-lang-en"
              >
                <Printer className="w-5 h-5" />
                <span>English</span>
                <span className="text-[10px] text-sky-300/70 font-mono">EN</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}


function KpiCard({ label, value, Icon, color, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border p-3.5"
      style={{
        borderColor: `${color}40`,
        background: `linear-gradient(135deg, ${color}12 0%, ${color}03 100%)`,
      }}
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider mb-1.5"
           style={{ color }}>
        <Icon className="w-3.5 h-3.5" />
        <span className="line-clamp-1">{label}</span>
      </div>
      <div className="text-lg lg:text-xl font-bold text-white font-mono">
        {fmt2(value)}
        <span className="text-[11px] text-white/50 ms-1.5">AED</span>
      </div>
    </motion.div>
  );
}


// iter-226 (Feb 2026) — Audit cell.
// iter-227 (Feb 2026) — Formula updated: audit_ok now compares
// (gov_fees + service_fee) against amount_received; commission is NOT
// part of the audit total any more (per user's spec — commission is our
// internal cost, not part of what the customer pays).
function AuditCell({ r, t }) {
  const ok       = !!r.audit_ok;
  const delta    = Number(r.audit_delta_aed || 0);
  const sum      = Number(r.audit_sum_aed || 0);
  const expected = Number(r.audit_expected_aed || 0);

  if (ok) {
    return (
      <span
        className="inline-flex items-center gap-1 text-emerald-400"
        title={t(
          `مدقّق ✓ — الرسوم الحكومية + رسوم الخدمة (${fmt2(sum)}) = المبلغ المستلم`,
          `Audited ✓ — Gov + Service (${fmt2(sum)}) matches received`
        )}
        data-testid={`revenue-audit-${r.receipt_no}-ok`}
      >
        <CheckCircle2 className="w-5 h-5" />
      </span>
    );
  }

  const missing = delta > 0;   // positive delta = something is missing from the breakdown
  const absAmt  = fmt2(Math.abs(delta));
  const tooltip = missing
    ? t(
        `الرسوم الحكومية + رسوم الخدمة = ${fmt2(sum)} — أقل من المبلغ المستلم بـ ${absAmt} AED. المبلغ الناقص لم يُوزَّع بين الأعمدة.`,
        `Gov + Service = ${fmt2(sum)} — SHORT by ${absAmt} AED vs received (${fmt2(expected)}). Missing amount not allocated across the columns.`
      )
    : t(
        `الرسوم الحكومية + رسوم الخدمة = ${fmt2(sum)} — أكبر من المبلغ المستلم بـ ${absAmt} AED. تم توزيع مبلغ زائد لم يُحصَّل.`,
        `Gov + Service = ${fmt2(sum)} — OVER by ${absAmt} AED vs received (${fmt2(expected)}). Breakdown exceeds what was collected.`
      );

  return (
    <span
      className="inline-flex items-center gap-1 text-red-400 cursor-help"
      title={tooltip}
      data-testid={`revenue-audit-${r.receipt_no}-fail`}
    >
      <XCircle className="w-5 h-5" />
    </span>
  );
}


/* iter-257b (Feb 2026) — Compact consistency comparison cell for the
 * stats box. Highlights a mismatch in amber so the operator can
 * immediately act (either close missing tasks or run the "Re-sync"
 * button which now auto-mints + cleans orphans). */
function StatsBoxCell({ title, closedLabel, closed, receipts, t, testid }) {
  const ok = closed === receipts;
  const diff = Math.max(0, closed - receipts);
  return (
    <div className={`rounded-xl border px-3 py-2 ${ok
      ? 'bg-emerald-500/[0.04] border-emerald-500/25'
      : 'bg-amber-500/[0.05] border-amber-500/30'
    }`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-[10px] text-white/60 font-semibold uppercase tracking-wider">{title}</p>
        {ok ? (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">
            OK
          </span>
        ) : (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/25">
            {t(`${diff} ناقص`, `${diff} missing`)}
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[9px] text-white/40 truncate">{closedLabel}</p>
          <p className={`text-xl font-bold ${ok ? 'text-emerald-300' : 'text-amber-300'}`}>{closed}</p>
        </div>
        <span className="text-white/30 text-sm">↔</span>
        <div className="flex-1 min-w-0 text-end">
          <p className="text-[9px] text-white/40">{t('الإيصالات', 'Receipts')}</p>
          <p className={`text-xl font-bold ${ok ? 'text-emerald-300' : 'text-amber-300'}`}>{receipts}</p>
        </div>
      </div>
    </div>
  );
}
